import {IAppBuilderWidgetPropsStackUi} from "@AppBuilderLib/features/appbuilder/config/appbuilder";
import {AppBuilderStackContext} from "@AppBuilderLib/features/appbuilder/lib/StackContext";
import type {MantineButtonProps} from "@AppBuilderLib/shared/mantine-props/button";
import type {MantinePaperProps} from "@AppBuilderLib/shared/mantine-props/paper";
import type {MantineStackProps} from "@AppBuilderLib/shared/mantine-props/stack";
import type {MantineTextProps} from "@AppBuilderLib/shared/mantine-props/text";
import Icon from "@AppBuilderLib/shared/ui/icon/Icon";
import {IconProps} from "@AppBuilderLib/shared/ui/icon/Icon.types";
import TooltipWrapper from "@AppBuilderLib/shared/ui/tooltip/TooltipWrapper";
import {Button, Paper, Stack, Text, useProps} from "@mantine/core";
import {useContext} from "react";

export interface StyleProps {
	/**
	 * Props for the Paper component wrapping the stack.
	 */
	stackPaperProps?: MantinePaperProps;
	/**
	 * Props for the Stack component containing buttons.
	 */
	stackProps?: MantineStackProps;
	/**
	 * Props for the icon on the right of the stack buttons.
	 */
	iconForwardProps?: IconProps;
	/**
	 * Props for the text of the stack buttons.
	 */
	itemTextProps?: MantineTextProps;
	/**
	 * Props for stack buttons.
	 */
	buttonForwardProps?: MantineButtonProps;
	/**
	 * Props for the back button.
	 */
	buttonBackProps?: MantineButtonProps;
}

const defaultStyleProps: Partial<StyleProps> = {
	stackPaperProps: {
		px: 0,
		py: 0,
		withBorder: false,
		shadow: "md",
		style: {backgroundColor: "var(--mantine-color-body)"},
	},
	stackProps: {gap: "xs"},
	itemTextProps: {size: "md", c: "var(--mantine-color-text)"},
	buttonForwardProps: {
		justify: "space-between",
		fullWidth: true,
		size: "lg",
		px: "md",
		variant: "default",
	},
	iconForwardProps: {
		size: 18,
		iconType: "tabler:chevron-right",
	},
};

type Props = IAppBuilderWidgetPropsStackUi &
	Partial<StyleProps> & {
		namespace: string;
	};

export default function AppBuilderStackUiWidgetButtonComponent(props: Props) {
	const {name, icon, tooltip, ...styleProps} = props;
	const {
		stackPaperProps,
		stackProps,
		itemTextProps,
		buttonForwardProps,
		iconForwardProps,
	} = useProps(
		"AppBuilderStackUiWidgetComponent",
		defaultStyleProps,
		styleProps,
	);
	const stackContext = useContext(AppBuilderStackContext);

	return (
		<Paper {...stackPaperProps}>
			<Stack {...stackProps}>
				<Button
					onClick={() => stackContext.push(name)}
					rightSection={
						<Icon
							{...iconForwardProps}
							iconType={
								iconForwardProps?.iconType ||
								"tabler:chevron-right"
							}
						/>
					}
					leftSection={icon ? <Icon iconType={icon} /> : undefined}
					{...buttonForwardProps}
					styles={{
						...(icon ? {label: {flex: 1}} : undefined),
						root: {
							// Prevents 1px press translateY from flashing a scrollbar in tight containers.
							"&:active": {transform: "none"},
							"&[data-active]": {transform: "none"},
						},
					}}
				>
					<Text {...itemTextProps}>
						{tooltip ? (
							<TooltipWrapper label={tooltip}>
								<div>{name}</div>
							</TooltipWrapper>
						) : (
							name
						)}
					</Text>
				</Button>
			</Stack>
		</Paper>
	);
}
